/**
 * VAL-PATH-ADJUST P4b — "Rates used". Step 3 mounts it read-only (the owner
 * sees the rates, never edits them, D5). The edit mode keeps the shipped
 * Intake labels, hints and the dental guide under Company-specific risk; it is
 * built here and mounted by P7b on the signer's screen.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CsrpGuide } from './_CsrpGuide'
import { Field } from './_AdjustLegs'

export interface RatesValues {
  riskFree: string
  companySpecificRisk: string
  equityRiskPremium: string
  sizePremium: string
  longTermGrowth: string
  growthRate: string
  projectionYears: string
  capWeight: string
}

type RateKey = keyof RatesValues

const RATES: Array<{ key: RateKey; id: string; label: string; editLabel: string; hint?: string }> = [
  { key: 'riskFree', id: 'risk_free_rate', label: 'Risk-free rate', editLabel: 'Risk-free rate * (20-yr Treasury)' },
  { key: 'companySpecificRisk', id: 'company_specific_risk', label: 'Company-specific risk', editLabel: 'Company-specific risk *' },
  { key: 'equityRiskPremium', id: 'equity_risk_premium', label: 'Equity risk premium', editLabel: 'Equity risk premium', hint: 'Blank = Kroll ERP.' },
  { key: 'sizePremium', id: 'size_premium', label: 'Size premium', editLabel: 'Size premium', hint: "Blank = Kroll Decile 10 constant (a documented floor, not fitted to this company's size)." },
  { key: 'longTermGrowth', id: 'long_term_growth_rate', label: 'Long-term growth rate', editLabel: 'Long-term growth rate *', hint: 'Must not exceed long-run GDP growth — Valuation Signer judgment.' },
  { key: 'growthRate', id: 'growth_rate', label: 'DCF growth rate', editLabel: 'DCF growth rate *' },
  { key: 'projectionYears', id: 'projection_years', label: 'Projection years', editLabel: 'Projection years' },
  { key: 'capWeight', id: 'capitalized_earnings_weight', label: 'Capitalized-earnings weight', editLabel: 'Capitalized-earnings weight', hint: 'Fraction. Balance goes to the DCF leg within the income approach.' },
]

export function RatesSection({
  values, industry, edit,
}: {
  values: RatesValues
  industry?: string | null
  /** Present = editable inputs; absent = read-only list. */
  edit?: (key: RateKey, value: string) => void
}) {
  return (
    <section aria-labelledby="rates-used-title">
      <Card>
        <CardHeader><CardTitle id="rates-used-title">Rates used</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {edit ? (
            <>
              <p className="text-xs text-pal-ink-muted">
                Percent scale — enter 4.35 for 4.35%. Leave the Kroll inputs blank to use the
                signed Kroll Cost of Capital Navigator defaults.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {RATES.map(r => (
                  <div key={r.key} className="contents">
                    <Field id={r.id} label={r.editLabel} value={values[r.key]} onChange={v => edit(r.key, v)} hint={r.hint} />
                    {r.key === 'companySpecificRisk' && <CsrpGuide industry={industry} value={values.companySpecificRisk} />}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {RATES.map(r => (
                <div key={r.key} className="flex justify-between gap-4">
                  <dt className="text-pal-ink-muted">{r.label}</dt>
                  <dd className="tabular-nums">{values[r.key].trim() || '—'}</dd>
                </div>
              ))}
            </dl>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
